import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search, RotateCcw } from 'lucide-react';
import { apiClient } from '../../apiClient';

const AdminBillingList = () => {
    const initialFilters = {
        billwaveCustomerId: '',
        company_name: '',
        invoice_number: '',
        stripe_invoice_id: '',
        status: '',
        billing_month: ''
    };

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastDocId, setLastDocId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState(initialFilters);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async (isNext = false) => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });
            queryParams.append('limit', 50);
            if (isNext && lastDocId) {
                queryParams.append('lastDocId', lastDocId);
            }

            const response = await apiClient.get(`/v1/admin/billing?${queryParams.toString()}`);
            const data = await response.json();

            if (response.ok) {
                if (isNext) {
                    setHistory(prev => [...prev, ...data]);
                } else {
                    setHistory(data);
                }
                if (data.length < 50) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                    setLastDocId(data[data.length - 1].id);
                }
            } else {
                setError(data.error || "データの取得に失敗しました。");
            }
        } catch (error) {
            console.error("Error fetching billing history:", error);
            setError("通信エラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [filters, lastDocId]);

    useEffect(() => {
        fetchHistory();
    }, []);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setLastDocId(null);
        setHasMore(true);
        fetchHistory();
    };

    const handleReset = () => {
        setFilters(initialFilters);
        setLastDocId(null);
        setHasMore(true);
        // Reset doesn't immediately trigger fetch due to closure, so we call it with empty filters
        const emptyFilters = initialFilters;
        // setFilters is async, so we use a trick or just wait
        setTimeout(() => {
            fetchHistory();
        }, 0);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const getStatusBadge = (status) => {
        const statuses = {
            paid: { label: '支払済', class: 'bg-green-100 text-green-800' },
            open: { label: '請求中', class: 'bg-blue-100 text-blue-800' },
            draft: { label: '下書き', class: 'bg-gray-100 text-gray-800' },
            void: { label: '無効', class: 'bg-red-100 text-red-800' },
            uncollectible: { label: '回収不能', class: 'bg-orange-100 text-orange-800' }
        };
        const s = statuses[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
        return <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${s.class}`}>{s.label}</span>;
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">請求管理</h2>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                    <p className="text-sm text-red-700 font-bold">{error}</p>
                </div>
            )}

            {/* 検索フォーム */}
            <form onSubmit={handleSearch} className="bg-white p-6 rounded-lg shadow-md mb-8 border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">会社名</label>
                        <input type="text" name="company_name" value={filters.company_name} onChange={handleFilterChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">BillWave顧客ID</label>
                        <input type="text" name="billwaveCustomerId" value={filters.billwaveCustomerId} onChange={handleFilterChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">請求書番号 / Stripe Invoice ID</label>
                        <input type="text" name="stripe_invoice_id" value={filters.stripe_invoice_id} onChange={handleFilterChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="in_..." />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">ステータス</label>
                        <select name="status" value={filters.status} onChange={handleFilterChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                            <option value="">すべて</option>
                            <option value="paid">支払済</option>
                            <option value="open">請求中</option>
                            <option value="unpaid">未払い (請求中/回収不能)</option>
                            <option value="void">無効</option>
                            <option value="uncollectible">回収不能</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">請求対象月</label>
                        <input type="month" name="billing_month" value={filters.billing_month} onChange={handleFilterChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                    </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <button type="submit" className="flex items-center bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-bold shadow-sm">
                        <Search size={18} className="mr-2" />
                        検索
                    </button>
                    <button type="button" onClick={handleReset} className="flex items-center bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 font-bold">
                        <RotateCcw size={18} className="mr-2" />
                        リセット
                    </button>
                </div>
            </form>

            <div className="bg-white shadow overflow-x-auto sm:rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">発行日</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">顧客ID / 会社名</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">請求金額</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">支払期限</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {history.map(item => (
                            <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {item.processedAt ? new Date(item.processedAt).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">{item.companyName}</div>
                                    <div className="text-xs text-gray-500">{item.accountId}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                    ¥{item.total?.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {item.billingPeriodEnd ? new Date(item.billingPeriodEnd).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(item.status)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link to={`/admin/billing/${item.invoiceId}`} className="text-indigo-600 hover:text-indigo-900 flex items-center justify-end">
                                        詳細 <ChevronRight size={16} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {history.length === 0 && !loading && (
                    <div className="text-center py-12 text-gray-500">
                        データが見つかりませんでした。
                    </div>
                )}
            </div>

            {hasMore && (
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={() => fetchHistory(true)}
                        disabled={loading}
                        className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 shadow-sm disabled:opacity-50"
                    >
                        {loading ? '読み込み中...' : '次の50件を読み込む'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminBillingList;
